import type { ServiceAreaSlug } from "./service-areas";

export type CampaignService = "roof-replacement" | "roof-repair" | "metal-roofing" | "flat-roofing";

export type PpcFaq = {
  question: string;
  answer: string;
};

export type PpcCampaign = {
  id: string;
  slug: string;
  /** Must stay false until written business approval. */
  enabled: boolean;
  /** When false, pages emit noindex and are excluded from the sitemap. */
  indexable: boolean;
  service: CampaignService;
  serviceName: string;
  servicePath: string;
  /** Confirmed service-area slug for local context (not a branch office claim). */
  location: ServiceAreaSlug;
  campaignIntent: string;
  headline: string;
  ctaLabel: string;
  faqs: readonly PpcFaq[];
  /** Optional verified project slug; null until real evidence is approved. */
  featuredProject: string | null;
  metaTitle: string;
  metaDescription: string;
  valueProposition: string;
  problemStatement: string;
  solutionSummary: string;
};

/**
 * PPC landing pages. `indexable` controls robots, sitemap inclusion, and public
 * rendering; keep `enabled` aligned when a campaign is live for ads.
 */
export const ppcCampaigns = [
  {
    id: "roof-replacement-vancouver",
    slug: "roof-replacement-vancouver",
    service: "roof-replacement",
    serviceName: "Roof Replacement",
    servicePath: "/services/asphalt-shingle-replacement",
    location: "vancouver",
    campaignIntent: "Vancouver homeowners evaluating a full roof replacement",
    headline: "Roof Replacement in Vancouver, BC",
    ctaLabel: "Request a Roof Assessment",
    faqs: [
      {
        question: "Does this page mean Revolin has a Vancouver office?",
        answer:
          "No. Vancouver is a confirmed service area. Revolin Roofing does not claim a physical branch office from this campaign page.",
      },
      {
        question: "Will you recommend replacement before assessing the roof?",
        answer:
          "No. Replacement is discussed only after the existing covering, flashings, drainage and related details are reviewed in context.",
      },
    ],
    featuredProject: null,
    metaTitle: "Vancouver Roof Replacement | Revolin Roofing",
    metaDescription:
      "Request an assessment for a Vancouver roof replacement. Understand the existing condition, system options and written scope before work begins.",
    valueProposition:
      "A documented roof assessment and a clear replacement scope tailored to the home.",
    problemStatement:
      "Widespread wear, repeated failures or multiple aging details can make isolated repairs less practical.",
    solutionSummary:
      "Revolin evaluates the roof covering, flashings, drainage, penetrations and transitions, then explains whether replacement is supported by the observed condition.",
    enabled: true,
    indexable: true,
  },
  {
    id: "roof-repair-vancouver",
    slug: "roof-repair-vancouver",
    service: "roof-repair",
    serviceName: "Roof Repair",
    servicePath: "/services/emergency-roof-leak-repair",
    location: "vancouver",
    campaignIntent: "Vancouver homeowners seeking diagnosis of a leak or isolated roof damage",
    headline: "Roof Repair in Vancouver, BC",
    ctaLabel: "Request a Roof Assessment",
    faqs: [
      {
        question: "Can a repair be scoped from photos alone?",
        answer:
          "Photos help, but a useful repair recommendation still depends on reviewing the reported area, adjacent planes and the likely water path.",
      },
      {
        question: "Is every leak a candidate for localized repair?",
        answer:
          "No. Some conditions point to broader renewal. The assessment explains which category fits the observed failure.",
      },
    ],
    featuredProject: null,
    metaTitle: "Vancouver Roof Repair & Leak Assessment | Revolin Roofing",
    metaDescription:
      "Request a Vancouver roof repair assessment for leaks, flashing concerns, penetrations or localized damage. Diagnosis comes before the repair scope.",
    valueProposition:
      "Trace the likely source before defining the repair - not just the location of the stain.",
    problemStatement:
      "Visible water can travel from a failed transition or penetration before it appears indoors.",
    solutionSummary:
      "The assessment reviews the reported area, adjacent roof planes and drainage paths to determine whether a localized repair is technically reasonable.",
    enabled: true,
    indexable: true,
  },
  {
    id: "roof-replacement-burnaby",
    slug: "roof-replacement-burnaby",
    service: "roof-replacement",
    serviceName: "Roof Replacement",
    servicePath: "/services/asphalt-shingle-replacement",
    location: "burnaby",
    campaignIntent: "Burnaby homeowners evaluating a full roof replacement",
    headline: "Roof Replacement in Burnaby, BC",
    ctaLabel: "Request a Roof Assessment",
    faqs: [
      {
        question: "Does Revolin operate a Burnaby branch from this page?",
        answer:
          "No. Burnaby is listed as a service area. This page does not claim a local retail office.",
      },
      {
        question: "What does a replacement assessment cover?",
        answer:
          "The covering, transitions, drainage, access and system suitability are reviewed before a written replacement scope is discussed.",
      },
    ],
    featuredProject: null,
    metaTitle: "Burnaby Roof Replacement | Revolin Roofing",
    metaDescription:
      "Plan a Burnaby roof replacement with a documented assessment, system recommendation and written scope based on the home and site.",
    valueProposition:
      "Replacement planning that accounts for the roof, access, drainage and architectural character.",
    problemStatement:
      "A roof with repeated failures or system-wide wear needs a broader decision than another isolated patch.",
    solutionSummary:
      "Revolin documents the current assembly and key details, compares suitable system categories and defines the work in writing.",
    enabled: true,
    indexable: true,
  },
  {
    id: "roof-repair-burnaby",
    slug: "roof-repair-burnaby",
    service: "roof-repair",
    serviceName: "Roof Repair",
    servicePath: "/services/emergency-roof-leak-repair",
    location: "burnaby",
    campaignIntent: "Burnaby homeowners seeking diagnosis of a roof leak or localized failure",
    headline: "Roof Repair in Burnaby, BC",
    ctaLabel: "Request a Roof Assessment",
    faqs: [
      {
        question: "Why inspect beyond the wet spot?",
        answer:
          "Water often travels. Adjacent flashings, valleys and penetrations can be part of the same failure even when the stain is localized.",
      },
      {
        question: "Will you always recommend repair?",
        answer:
          "No. If the condition supports renewal instead, that recommendation is explained with the observed limits of a localized fix.",
      },
    ],
    featuredProject: null,
    metaTitle: "Burnaby Roof Repair & Leak Assessment | Revolin Roofing",
    metaDescription:
      "Request a Burnaby roof repair assessment for leaks, flashing issues, penetrations or isolated damage, with a clear recommended scope.",
    valueProposition:
      "An assessment-led repair recommendation based on the observed failure and surrounding roof condition.",
    problemStatement:
      "Leaks at additions, valleys or lower roof sections may originate away from the point where water is visible.",
    solutionSummary:
      "Revolin follows the likely water path, documents adjacent details and explains the limits of the proposed repair.",
    enabled: true,
    indexable: true,
  },
  {
    id: "metal-roofing-vancouver",
    slug: "metal-roofing-vancouver",
    service: "metal-roofing",
    serviceName: "Metal Roofing",
    servicePath: "/services/metal-roof-replacement",
    location: "vancouver",
    campaignIntent: "Vancouver homeowners considering a standing-seam or architectural metal roof",
    headline: "Metal Roofing in Vancouver, BC",
    ctaLabel: "Request a Roof Assessment",
    faqs: [
      {
        question: "Is metal always the best upgrade?",
        answer:
          "No. Suitability depends on slope, geometry, transitions and the underlying assembly. Metal is evaluated as a complete system, not a default finish.",
      },
      {
        question: "What gets reviewed before recommending metal?",
        answer:
          "Roof geometry, detailing constraints, drainage and design priorities are reviewed before a system category is recommended.",
      },
    ],
    featuredProject: null,
    metaTitle: "Vancouver Metal Roofing Assessment | Revolin Roofing",
    metaDescription:
      "Explore architectural metal roofing for a Vancouver home. Revolin assesses slope, geometry, transitions and design priorities before recommending a system.",
    valueProposition:
      "Architectural metal roofing considered as a complete assembly, not a finish selected in isolation.",
    problemStatement:
      "Metal roofing can be a strong premium option, but suitability depends on slope, geometry, transitions and the underlying assembly.",
    solutionSummary:
      "Revolin reviews the building and explains system, profile and detailing considerations without presenting metal as universally best.",
    enabled: true,
    indexable: true,
  },
  {
    id: "flat-roofing-vancouver",
    slug: "flat-roofing-vancouver",
    service: "flat-roofing",
    serviceName: "Flat & Low-Slope Roofing",
    servicePath: "/services/flat-low-slope-roof-repair",
    location: "vancouver",
    campaignIntent: "Vancouver property owners assessing a flat or low-slope roof area",
    headline: "Flat & Low-Slope Roofing in Vancouver, BC",
    ctaLabel: "Request a Roof Assessment",
    faqs: [
      {
        question: "Why does drainage matter so much on low-slope roofs?",
        answer:
          "Ponding and failed transitions often drive the failure mode. Drainage paths are reviewed before repair or renewal options are discussed.",
      },
      {
        question: "Are flat and low-slope areas scoped the same way as steep slopes?",
        answer:
          "No. Membrane areas, curbs, parapets and wall interfaces need their own assessment focus.",
      },
    ],
    featuredProject: null,
    metaTitle: "Vancouver Flat & Low-Slope Roofing | Revolin Roofing",
    metaDescription:
      "Request an assessment for a Vancouver flat or low-slope roof. Review drainage, membrane areas, curbs, parapets and transitions before defining scope.",
    valueProposition:
      "Low-slope roofing planned around drainage and transitions, not treated as a flat surface alone.",
    problemStatement:
      "Ponding, failed transitions or membrane damage can have different causes and do not support the same repair by default.",
    solutionSummary:
      "Revolin documents drainage, membrane condition, penetrations, curbs and wall interfaces before discussing repair or renewal options.",
    enabled: true,
    indexable: true,
  },
] as const satisfies readonly PpcCampaign[];

export type PpcCampaignId = (typeof ppcCampaigns)[number]["id"];

export const enabledPpcCampaigns = ppcCampaigns.filter((campaign) => campaign.enabled);

export function findPpcCampaign(slug: string | undefined): PpcCampaign | undefined {
  return ppcCampaigns.find((campaign) => campaign.slug === slug);
}

/** Robots directive derived from the campaign indexable flag. */
export function ppcCampaignRobots(campaign: PpcCampaign): "index, follow" | "noindex, nofollow" {
  return campaign.indexable ? "index, follow" : "noindex, nofollow";
}
